/* =========================================================================
   Persistent guest identity. Every unauthenticated device gets a stable UUID
   (localStorage `catholics.guestId`) sent with all guest telemetry + served-
   question logging — this is what powers the admin "active guests/day",
   unique-IP and live-now views. Survives reloads; regenerated only after an
   account deletion (resetGuestId).
   ========================================================================= */

const GUEST_ID_KEY = 'catholics.guestId';

/** RFC-4122 v4 UUID. Prefers crypto.randomUUID; falls back to a getRandomValues
    build for older WebViews (and a Math.random last resort so it never throws). */
function newUuidV4(): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  const b = new Uint8Array(16);
  if (c && typeof c.getRandomValues === 'function') c.getRandomValues(b);
  else for (let i = 0; i < 16; i++) b[i] = Math.floor(Math.random() * 256);
  b[6] = (b[6] & 0x0f) | 0x40; // version 4
  b[8] = (b[8] & 0x3f) | 0x80; // variant 10xx
  const h = Array.from(b, (x) => x.toString(16).padStart(2, '0'));
  return `${h[0]}${h[1]}${h[2]}${h[3]}-${h[4]}${h[5]}-${h[6]}${h[7]}-${h[8]}${h[9]}-${h[10]}${h[11]}${h[12]}${h[13]}${h[14]}${h[15]}`;
}

let cached: string | null = null;

/** The device's persistent guest id, minting + storing one on first use. */
export function getGuestId(): string {
  if (cached) return cached;
  try {
    const existing = localStorage.getItem(GUEST_ID_KEY);
    if (existing) {
      cached = existing;
      return existing;
    }
  } catch {
    /* private mode — fall through to an in-memory id */
  }
  const id = newUuidV4();
  cached = id;
  try {
    localStorage.setItem(GUEST_ID_KEY, id);
  } catch {
    /* storage unavailable — id lives only for this page load */
  }
  return id;
}

/** Forget the current guest id (used after account deletion so the freshly
    anonymized device is counted as a brand-new guest). */
export function resetGuestId(): void {
  cached = null;
  try {
    localStorage.removeItem(GUEST_ID_KEY);
  } catch {
    /* noop */
  }
}
